import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { hasPermission } from '../utils/permissions';
import {
  BUSINESS_TYPE_OPTIONS,
  DEFAULT_BUSINESS_TYPE,
  SMALL_BUSINESS_TYPE,
  OTHER_BUSINESS_TYPE,
  ZIP_LOOKUP_URL,
  CONGRESSIONAL_DISTRICT_URL,
  isValidZip,
  isValidCongressionalDistrict
} from '../utils/businessTypes';

// Combine address parts into a single comma-separated string
const combineAddressParts = ({ line1 = '', line2 = '', city = '', state = '', zip = '' } = {}) => {
  const lineCombo = [line1, line2].filter(Boolean).join(', ');
  const csz = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [lineCombo, csz].filter(Boolean).join(', ');
};

// Display split address parts as multi-line text. Falls back to a single
// legacy combined string when none of the structured fields are populated.
const SplitAddressDisplay = ({ parts = {}, fallback }) => {
  const { line1, line2, city, state, zip } = parts;
  const hasAny = [line1, line2, city, state, zip].some(v => v && String(v).trim() !== '');
  if (!hasAny) return <p className="text-gray-900 whitespace-pre-line">{fallback || 'N/A'}</p>;
  return (
    <div className="text-gray-900 leading-tight">
      {line1 && <div>{line1}</div>}
      {line2 && <div>{line2}</div>}
      <div>{[city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</div>
    </div>
  );
};

// 5-input editor for address parts, mirroring the formation form's AddressFieldGroup.
const SplitAddressEditor = ({ values = {}, onChange, showZipHelp = true }) => {
  const update = (key) => (e) => onChange({ ...values, [key]: e.target.value });
  const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";
  const zipValid = isValidZip(values.zip);
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
      <div className="md:col-span-6">
        <label className="block text-xs text-gray-600 mb-0.5">Address Line 1</label>
        <input type="text" className={inputCls} value={values.line1 || ''} onChange={update('line1')} />
      </div>
      <div className="md:col-span-6">
        <label className="block text-xs text-gray-600 mb-0.5">Address Line 2 (optional)</label>
        <input type="text" className={inputCls} value={values.line2 || ''} onChange={update('line2')} />
      </div>
      <div className="md:col-span-3">
        <label className="block text-xs text-gray-600 mb-0.5">City</label>
        <input type="text" className={inputCls} value={values.city || ''} onChange={update('city')} />
      </div>
      <div className="md:col-span-1">
        <label className="block text-xs text-gray-600 mb-0.5">State</label>
        <input type="text" className={inputCls} value={values.state || ''} onChange={update('state')} />
      </div>
      <div className="md:col-span-2">
        <label className="block text-xs text-gray-600 mb-0.5">ZIP Code</label>
        <input
          type="text"
          className={zipValid ? inputCls : `${inputCls} border-red-500`}
          placeholder="#####-####"
          value={values.zip || ''}
          onChange={update('zip')}
        />
        {!zipValid && (
          <p className="text-xs text-red-600 mt-1">Use the format #####-#### (or #####).</p>
        )}
        {showZipHelp && (
          <p className="text-xs text-gray-500 mt-1">
            Format: #####-#### &middot;{' '}
            <a href={ZIP_LOOKUP_URL} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              find the 4-digit extension
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

// Congressional district entry for a performance site, with the House lookup link.
const CongressionalDistrictField = ({ value, onChange, editing }) => (
  <div className="mt-2">
    <label className="block text-xs text-gray-600 mb-0.5">
      Congressional District (e.g., CA-012){' '}
      <a href={CONGRESSIONAL_DISTRICT_URL} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
        (click here to find)
      </a>
    </label>
    {editing ? (
      <>
        <input
          type="text"
          className={
            'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 '
            + (isValidCongressionalDistrict(value) ? 'border-gray-300' : 'border-red-500')
          }
          placeholder="CA-012"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
        {!isValidCongressionalDistrict(value) && (
          <p className="text-xs text-red-600 mt-1">Use the format XX-### (e.g., CA-012).</p>
        )}
      </>
    ) : (
      <p className="text-gray-900">{value || 'N/A'}</p>
    )}
  </div>
);

// Each role can point at exactly one business address (stored as an index,
// 0 = primary). With a single address every role is pinned to it.
const ADDRESS_ROLES = [
  { key: 'irsAddressIndex', label: 'IRS Address' },
  { key: 'mailingAddressIndex', label: 'Preferred Mailing Address' },
  { key: 'grantsGovAddressIndex', label: 'Grants.gov Address' }
];

const AddressRoleCheckboxes = ({ index, total, values = {}, editing, onSelect }) => {
  const locked = total <= 1;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
      {ADDRESS_ROLES.map(role => {
        const checked = locked || (Number(values[role.key]) || 0) === index;
        return (
          <label key={role.key} className={`inline-flex items-center text-sm ${checked ? 'text-gray-900' : 'text-gray-500'}`}>
            <input
              type="checkbox"
              className="mr-1.5"
              checked={checked}
              disabled={!editing || locked}
              // Unchecking is a no-op: a role always belongs to one address, and
              // moves only when it is checked on another one.
              onChange={(e) => { if (e.target.checked) onSelect(role.key, index); }}
            />
            {role.label}
          </label>
        );
      })}
    </div>
  );
};

const NAICS_SEARCH_URL = 'https://www.naics.com/search/';

const CompanyInfo = ({ userInfo }) => {
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banks, setBanks] = useState([]);
  // Banks are edited one card at a time: editingBankIndex points at the bank
  // being changed (or the new-bank slot while isAddingBank is set) and
  // editedBank holds the in-progress copy.
  const [editingBankIndex, setEditingBankIndex] = useState(null);
  const [isAddingBank, setIsAddingBank] = useState(false);
  const [editedBank, setEditedBank] = useState(null);
  const [savingBank, setSavingBank] = useState(false);
  // Account/routing numbers are masked until the viewer asks to see them.
  const [revealedBankFields, setRevealedBankFields] = useState({});

  // Founder info state
  const [isEditingFounder, setIsEditingFounder] = useState(null); // index of founder being edited
  const [editedFounder, setEditedFounder] = useState(null);
  const [savingFounder, setSavingFounder] = useState(false);
  const [ssnVisible, setSsnVisible] = useState({});
  const [skipOnboarding, setSkipOnboarding] = useState(false);

  // Mask all but the last 4 characters of sensitive values (account/routing numbers)
  const maskLast4 = (value) => {
    const str = String(value || '');
    if (!str) return '';
    if (str.length <= 4) return str;
    return '•'.repeat(str.length - 4) + str.slice(-4);
  };

  // Normalize a bank info object coming from the company formation flow into the
  // shape used by the Banks section. Formation data may arrive with camelCase
  // keys (from the submission) or Title Case keys (from bank_info.csv fallback).
  const normalizeFormationBank = (bi) => {
    if (!bi || typeof bi !== 'object') return null;
    const pick = (...keys) => {
      for (const k of keys) {
        if (bi[k] !== undefined && bi[k] !== null && String(bi[k]).trim() !== '') return bi[k];
      }
      return '';
    };
    const bank = {
      bankName: pick('bankName', 'Bank Name', 'bankType', 'Bank Type'),
      accountType: pick('accountType', 'Account Type'),
      accountNumber: pick('accountNumber', 'Account Number'),
      routingNumber: pick('routingNumber', 'Routing Number'),
      bankPhone: pick('bankPhone', 'Bank Phone'),
      bankAddress: pick('bankAddress', 'Bank Address'),
    };
    // Only return if there is at least some meaningful data
    const hasData = Object.values(bank).some(v => String(v).trim() !== '');
    return hasData ? bank : null;
  };

  // Mercury's known branch details (auto-filled but editable)
  const MERCURY_DEFAULTS = {
    routingNumber: '',
    bankPhone: '415-878-1068',
    bankAddress: '4501 23rd Avenue S\nFargo, ND 58104'
  };

  // Kept in a ref so a save can re-run exactly the same load the page starts with.
  const reloadCompanyInfoRef = React.useRef(null);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`
        );

        if (response.ok) {
          const data = await response.json();
          console.log("fetched company data:", data);
          if (data.skipOnboarding) {
            setSkipOnboarding(true);
          }

          // Backend already merges company users into founders list
          setCompanyData(data);

          // Fetch banks data
          const banksResponse = await api.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/company/banks`
          );
          let savedBanks = [];
          if (banksResponse.ok) {
            const banksData = await banksResponse.json();
            savedBanks = banksData.banks || [];
          }

          // If no banks have been saved to the Banks section yet, seed it from the
          // bank info collected during the company formation questionnaire so the
          // info entered there shows up here instead of being lost.
          if (savedBanks.length === 0) {
            const formationBank = normalizeFormationBank(data?.companyInfo?.bankInfo);
            if (formationBank) {
              savedBanks = [formationBank];
            }
          }
          setBanks(savedBanks);
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to fetch company information');
        }
      } catch (err) {
        console.error('Error fetching company info:', err);
        setError('Failed to load company information');
      } finally {
        setLoading(false);
      }
    };

    reloadCompanyInfoRef.current = fetchCompanyInfo;
    fetchCompanyInfo();
  }, []);

  // Entering edit mode writes the defaults the read-only view has been showing
  // into the draft, so an untouched drop-down still saves the value on screen.
  const handleEdit = () => {
    const draft = JSON.parse(JSON.stringify(companyData));
    const ci = draft.companyInfo || (draft.companyInfo = {});
    if (!ci.businessType) ci.businessType = DEFAULT_BUSINESS_TYPE;
    ci.smallBusinessWomanOwned = !!ci.smallBusinessWomanOwned;
    ci.smallBusinessDisadvantaged = !!ci.smallBusinessDisadvantaged;
    ci.performanceSiteSameAsBusiness = !!ci.performanceSiteSameAsBusiness;
    if (!Array.isArray(ci.additionalBusinessAddresses)) ci.additionalBusinessAddresses = [];
    if (isAdmin && ci.signingOfficialEmail === undefined) ci.signingOfficialEmail = companyData.soAorEmail || '';
    setEditedData(draft);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedData(null);
    setIsEditing(false);
  };

  const handleFieldChange = (section, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Every ZIP and congressional district on the page has to be well-formed before
  // the save is offered, so a malformed one is never written to the record.
  const validationErrors = (() => {
    if (!isEditing || !editedData) return [];
    const ci = editedData.companyInfo || {};
    const errors = [];
    if (!isValidZip(ci.businessAddressZip)) errors.push('Business address ZIP must look like #####-#### (or #####).');
    if (!isValidCongressionalDistrict(ci.businessAddressCongressionalDistrict)) {
      errors.push('Business address congressional district must look like XX-### (e.g., CA-012).');
    }
    (ci.additionalBusinessAddresses || []).forEach((addr, idx) => {
      if (!isValidZip(addr.zip)) errors.push(`Business address #${idx + 2} ZIP must look like #####-#### (or #####).`);
      if (!isValidCongressionalDistrict(addr.congressionalDistrict)) {
        errors.push(`Business address #${idx + 2} congressional district must look like XX-### (e.g., CA-012).`);
      }
    });
    if (!ci.performanceSiteSameAsBusiness && !isValidZip(ci.performanceSiteAddressZip)) {
      errors.push('Performance site ZIP must look like #####-#### (or #####).');
    }
    if (!isValidCongressionalDistrict(ci.performanceSiteCongressionalDistrict)) {
      errors.push('Primary performance site congressional district must look like XX-### (e.g., CA-012).');
    }
    (ci.additionalPerformanceSites || []).forEach((site, idx) => {
      if (!isValidZip(site.zip)) errors.push(`Performance site #${idx + 2} ZIP must look like #####-#### (or #####).`);
      if (!isValidCongressionalDistrict(site.congressionalDistrict)) {
        errors.push(`Performance site #${idx + 2} congressional district must look like XX-### (e.g., CA-012).`);
      }
    });
    return errors;
  })();

  const handleSaveClick = () => {
    if (validationErrors.length > 0) {
      alert(`Please fix the following before saving:\n\n- ${validationErrors.join('\n- ')}`);
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    try {
      setSaving(true);
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`,
        editedData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        await response.json();
        // The update endpoint answers with companyInfo/founders only, so reload the
        // full record — otherwise the SO/AOR block empties until the next refresh.
        if (reloadCompanyInfoRef.current) {
          await reloadCompanyInfoRef.current();
        }
        setIsEditing(false);
        setShowConfirmModal(false);
        setEditedData(null);
        alert('Company information updated successfully!');
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to update company information');
      }
    } catch (err) {
      console.error('Error saving company info:', err);
      alert('Failed to save company information');
    } finally {
      setSaving(false);
    }
  };

  const isAdmin = userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin';
  const currentUserEmail = userInfo?.email || userInfo?.username;

  const handleEditFounder = (index) => {
    setEditedFounder({ ...founders[index] });
    setIsEditingFounder(index);
  };

  const handleCancelFounderEdit = () => {
    setEditedFounder(null);
    setIsEditingFounder(null);
  };

  const handleFounderFieldChange = (field, value) => {
    setEditedFounder(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveFounder = async () => {
    try {
      setSavingFounder(true);
      // Build updated founders array
      const updatedFounders = [...founders];
      updatedFounders[isEditingFounder] = { ...editedFounder, isInvited: false };

      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`,
        {
          companyInfo: companyData.companyInfo,
          founders: updatedFounders
        },
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.ok) {
        const updatedData = await response.json();
        setCompanyData(prev => ({ ...prev, founders: updatedFounders }));
        setIsEditingFounder(null);
        setEditedFounder(null);
        alert('Founder information updated successfully!');
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to update founder information');
      }
    } catch (err) {
      console.error('Error saving founder info:', err);
      alert('Failed to save founder information');
    } finally {
      setSavingFounder(false);
    }
  };

  const canEditFounder = (founder) => {
    if (isAdmin) return true;
    return founder.email === currentUserEmail;
  };

  const canViewFounder = (founder) => {
    console.log("Can view founder check:", { founderEmail: founder.email, currentUserEmail, isAdmin });
    if (isAdmin) return true;
    return founder.email === currentUserEmail;
  };

  // Admins and super clients can unmask the account and routing numbers from the
  // read-only view, so nobody has to open the edit form just to read them.
  const canViewBankNumbers = isAdmin || userInfo?.accountType === 'super_client';

  // Restricted users only see EIN / bank info when a Founder or Super User has
  // checked those boxes for them on the user management page.
  const canViewEin = hasPermission(userInfo, 'ein');
  const canViewBankInfo = hasPermission(userInfo, 'bank_info');

  const EMPTY_BANK = {
    bankName: '',
    accountType: '',
    accountNumber: '',
    routingNumber: '',
    bankPhone: '',
    bankAddress: ''
  };

  // The banks endpoint always takes the full list, so every single-card change
  // is applied to a copy of the list and posted whole.
  const persistBanks = async (nextBanks, successMessage) => {
    try {
      setSavingBank(true);
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company/banks`,
        { banks: nextBanks }
      );
      if (response.ok) {
        setBanks(nextBanks);
        if (successMessage) alert(successMessage);
        return true;
      }
      const errorData = await response.json();
      alert(errorData.error || 'Failed to update banks');
      return false;
    } catch (err) {
      console.error('Error saving banks:', err);
      alert('Failed to save banks');
      return false;
    } finally {
      setSavingBank(false);
    }
  };

  const handleEditBank = (index) => {
    setIsAddingBank(false);
    setEditingBankIndex(index);
    setEditedBank({ ...EMPTY_BANK, ...banks[index] });
  };

  const handleAddBank = () => {
    setIsAddingBank(true);
    setEditingBankIndex(banks.length);
    setEditedBank({ ...EMPTY_BANK });
  };

  const handleCancelBankEdit = () => {
    setIsAddingBank(false);
    setEditingBankIndex(null);
    setEditedBank(null);
  };

  const handleBankFieldChange = (field, value) => {
    setEditedBank(prev => {
      const next = { ...prev, [field]: value };
      // Auto-fill Mercury's branch details (editable afterwards)
      if (field === 'bankName' && value === 'Mercury (Choice Financial Group)') {
        if (!next.bankPhone) next.bankPhone = MERCURY_DEFAULTS.bankPhone;
        if (!next.bankAddress) next.bankAddress = MERCURY_DEFAULTS.bankAddress;
      }
      return next;
    });
  };

  const handleSaveBank = async () => {
    const nextBanks = isAddingBank
      ? [...banks, editedBank]
      : banks.map((bank, i) => (i === editingBankIndex ? editedBank : bank));

    const saved = await persistBanks(
      nextBanks,
      isAddingBank ? 'Bank added successfully!' : 'Bank updated successfully!'
    );
    if (saved) handleCancelBankEdit();
  };

  const handleDeleteBank = async (index) => {
    if (!window.confirm('Remove this bank?')) return;
    const saved = await persistBanks(banks.filter((_, i) => i !== index), 'Bank removed successfully!');
    if (saved) handleCancelBankEdit();
  };

  // Account and routing numbers stay masked until the viewer clicks View.
  const renderSensitiveBankValue = (index, field, value) => {
    if (!value) return <p className="text-gray-900">N/A</p>;
    const key = `${index}-${field}`;
    const visible = !!revealedBankFields[key];
    return (
      <div className="flex items-center gap-2">
        <p className="text-gray-900 font-mono">{visible ? value : maskLast4(value)}</p>
        {canViewBankNumbers && (
          <button
            onClick={() => setRevealedBankFields(prev => ({ ...prev, [key]: !prev[key] }))}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            {visible ? 'Hide' : 'View'}
          </button>
        )}
      </div>
    );
  };

  const renderBankCard = (bank, index) => {
    const isEditingThis = editingBankIndex === index;
    const displayBank = isEditingThis ? editedBank : bank;
    const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";

    return (
      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-medium text-gray-900">
            {displayBank.bankName || (isAddingBank && isEditingThis ? 'New Bank' : 'Bank')}
          </h3>
          {isEditingThis ? (
            <div className="flex gap-2">
              <button
                onClick={handleCancelBankEdit}
                disabled={savingBank}
                className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBank}
                disabled={savingBank}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {savingBank ? 'Saving...' : 'Save'}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => handleEditBank(index)}
                disabled={isAddingBank || editingBankIndex !== null}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteBank(index)}
                disabled={isAddingBank || editingBankIndex !== null || savingBank}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Remove
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Bank Name</label>
            {isEditingThis ? (
              <select
                value={displayBank.bankName || ''}
                onChange={(e) => handleBankFieldChange('bankName', e.target.value)}
                className={inputCls}
              >
                <option value="">Select Bank</option>
                <option value="Mercury (Choice Financial Group)">Mercury (Choice Financial Group)</option>
                <option value="PNC">PNC</option>
                <option value="Bank of America">Bank of America</option>
              </select>
            ) : (
              <p className="text-gray-900">{displayBank.bankName || 'N/A'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Account Type</label>
            {isEditingThis ? (
              <select
                value={displayBank.accountType || ''}
                onChange={(e) => handleBankFieldChange('accountType', e.target.value)}
                className={inputCls}
              >
                <option value="">Select Type</option>
                <option value="Credit">Credit</option>
                <option value="Checking">Checking</option>
                <option value="Savings">Savings</option>
              </select>
            ) : (
              <p className="text-gray-900">{displayBank.accountType || 'N/A'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Account Number</label>
            {isEditingThis ? (
              <input
                type="text"
                value={displayBank.accountNumber || ''}
                onChange={(e) => handleBankFieldChange('accountNumber', e.target.value)}
                className={inputCls}
                placeholder="Enter account number"
              />
            ) : (
              renderSensitiveBankValue(index, 'accountNumber', displayBank.accountNumber)
            )}
          </div>
          {/* Credit cards have no routing number, and the bank's phone/address are
              only needed for checking accounts. */}
          {displayBank.accountType !== 'Credit' && (
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Routing Number</label>
            {isEditingThis ? (
              <input
                type="text"
                value={displayBank.routingNumber || ''}
                onChange={(e) => handleBankFieldChange('routingNumber', e.target.value)}
                className={inputCls}
                placeholder="Enter routing number"
              />
            ) : (
              renderSensitiveBankValue(index, 'routingNumber', displayBank.routingNumber)
            )}
          </div>
          )}
          {(!displayBank.accountType || displayBank.accountType === 'Checking') && (
          <>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Bank Phone</label>
            {isEditingThis ? (
              <input
                type="tel"
                value={displayBank.bankPhone || ''}
                onChange={(e) => handleBankFieldChange('bankPhone', e.target.value)}
                className={inputCls}
                placeholder="Enter bank phone"
              />
            ) : (
              <p className="text-gray-900">{displayBank.bankPhone || 'N/A'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Bank Address</label>
            {isEditingThis ? (
              <textarea
                value={displayBank.bankAddress || ''}
                onChange={(e) => handleBankFieldChange('bankAddress', e.target.value)}
                rows="2"
                className={inputCls}
                placeholder="Enter bank address"
              />
            ) : (
              <p className="text-gray-900 whitespace-pre-line">{displayBank.bankAddress || 'N/A'}</p>
            )}
          </div>
          </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading company information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!companyData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading company information...</p>
        </div>
      </div>
    );
  }

  const companyInfo = companyData.companyInfo || {};
  const founders = companyData.founders || [];
  // Founders this user is allowed to see. When there are none the whole section
  // is hidden, header included, rather than showing an empty card.
  const visibleFounders = founders.filter(canViewFounder);
  console.log("companyData:", companyData);
  const displayData = isEditing ? editedData : companyData;

  // Signing official details. The name is assigned on the super admin dashboard
  // and the phone is StreamLaunch's own number (admin dashboard); only admins can
  // change the email here.
  const signingOfficial = {
    phone: companyInfo.signingOfficialPhone || '',
    email: companyInfo.signingOfficialEmail || companyData.soAorEmail || ''
  };

  // Business addresses: the primary lives in the businessAddress* fields and any
  // extras in additionalBusinessAddresses, mirroring performance sites.
  const additionalBusinessAddresses = (displayData.companyInfo?.additionalBusinessAddresses || []);
  const totalBusinessAddresses = 1 + additionalBusinessAddresses.length;
  const handleAddressRoleSelect = (roleKey, index) => handleFieldChange('companyInfo', roleKey, index);
  const handleRemoveBusinessAddress = (idx) => {
    const next = [...additionalBusinessAddresses];
    next.splice(idx, 1);
    handleFieldChange('companyInfo', 'additionalBusinessAddresses', next);
    // Roles pointing at the removed address fall back to the primary; roles on
    // later addresses shift down with them.
    const removedIndex = idx + 1;
    ADDRESS_ROLES.forEach(({ key }) => {
      const current = Number(displayData.companyInfo?.[key]) || 0;
      if (current === removedIndex) handleFieldChange('companyInfo', key, 0);
      else if (current > removedIndex) handleFieldChange('companyInfo', key, current - 1);
    });
  };
  const updateBusinessAddress = (idx, patch) => {
    const next = [...additionalBusinessAddresses];
    next[idx] = { ...next[idx], ...patch };
    handleFieldChange('companyInfo', 'additionalBusinessAddresses', next);
  };

  const formatDateOnlyForInput = (dateValue) => {
    if (!dateValue) return '';

    if (typeof dateValue === 'string') {
      const datePart = dateValue.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return '';

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateOnlyForDisplay = (dateValue) => {
    const dateString = formatDateOnlyForInput(dateValue);
    if (!dateString) return 'N/A';

    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  };

  return (
    <div className="max-w-6xl mx-auto p-6 pt-0">
      {/* Sticky so Save Changes stays reachable while scrolling a long form. The
          negative margin stretches it over the page padding so the cards' shadows
          and borders can't peek out at its sides as they scroll underneath. */}
      <div className="sticky top-16 z-30 bg-gray-50 -mx-6 px-6 pt-6 pb-4 mb-4 flex justify-between items-start border-b border-gray-200">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Company Information</h1>
          <p className="text-gray-600 mt-2">
            {isEditing ? 'Edit your company formation details' : 'View your company formation details'}
          </p>
        </div>
        <div className="flex gap-2">
          {!isEditing ? (
            <>
              <button
                onClick={() => navigate('/company-formation')}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                Formation Questionnaire
              </button>
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Edit Information
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveClick}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Save Changes
              </button>
            </>
          )}
        </div>
      </div>

      {/* Company Details Card */}
      <div className="card p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Company Name</label>
            {isEditing ? (
              <input
                type="text"
                value={displayData.companyInfo?.companyName || ''}
                onChange={(e) => handleFieldChange('companyInfo', 'companyName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-gray-900">{companyInfo.companyName || 'N/A'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Date of Formation</label>
            {isEditing ? (
              <input
                type="date"
                value={formatDateOnlyForInput(displayData.companyInfo?.dateOfFormation)}
                onChange={(e) => handleFieldChange('companyInfo', 'dateOfFormation', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-gray-900">
                {formatDateOnlyForDisplay(companyInfo.dateOfFormation)}
              </p>
            )}
          </div>

          {canViewEin && (
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">EIN Number</label>
            {isEditing ? (
              <input
                type="text"
                value={displayData.companyInfo?.einNumber || ''}
                onChange={(e) => handleFieldChange('companyInfo', 'einNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-gray-900">{companyInfo.einNumber || 'N/A'}</p>
            )}
          </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-gray-700 mb-1">Type of Business</label>
            {isEditing ? (
              <>
                <select
                  value={displayData.companyInfo?.businessType || DEFAULT_BUSINESS_TYPE}
                  onChange={(e) => handleFieldChange('companyInfo', 'businessType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {BUSINESS_TYPE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {(displayData.companyInfo?.businessType || DEFAULT_BUSINESS_TYPE) === OTHER_BUSINESS_TYPE && (
                  <input
                    type="text"
                    value={displayData.companyInfo?.businessTypeOther || ''}
                    onChange={(e) => handleFieldChange('companyInfo', 'businessTypeOther', e.target.value)}
                    placeholder="Specify other type of business"
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
                {(displayData.companyInfo?.businessType || DEFAULT_BUSINESS_TYPE) === SMALL_BUSINESS_TYPE && (
                  <div className="mt-3 border rounded p-3 bg-gray-50">
                    <p className="text-sm font-bold text-gray-700 mb-2">Small Business Organization Type</p>
                    <label className="flex items-center text-sm text-gray-700 mb-1">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={!!displayData.companyInfo?.smallBusinessWomanOwned}
                        onChange={(e) => handleFieldChange('companyInfo', 'smallBusinessWomanOwned', e.target.checked)}
                      />
                      Woman Owned?
                    </label>
                    <label className="flex items-center text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={!!displayData.companyInfo?.smallBusinessDisadvantaged}
                        onChange={(e) => handleFieldChange('companyInfo', 'smallBusinessDisadvantaged', e.target.checked)}
                      />
                      Socially and Economically Disadvantaged?
                    </label>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-gray-900">
                  {companyInfo.businessType || DEFAULT_BUSINESS_TYPE}
                  {(companyInfo.businessType === OTHER_BUSINESS_TYPE && companyInfo.businessTypeOther)
                    ? `: ${companyInfo.businessTypeOther}`
                    : ''}
                </p>
                {(companyInfo.businessType || DEFAULT_BUSINESS_TYPE) === SMALL_BUSINESS_TYPE && (
                  <p className="text-sm text-gray-600 mt-1">
                    Small Business Organization Type:{' '}
                    {[
                      companyInfo.smallBusinessWomanOwned ? 'Woman Owned' : null,
                      companyInfo.smallBusinessDisadvantaged ? 'Socially and Economically Disadvantaged' : null
                    ].filter(Boolean).join(', ') || 'None selected'}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-gray-700 mb-1">
              {totalBusinessAddresses > 1 ? 'Business Addresses' : 'Business Address'}
            </label>
            {isEditing ? (
              <>
                <div className="border rounded p-3 mb-3">
                  {totalBusinessAddresses > 1 && (
                    <p className="text-xs font-semibold text-gray-700 mb-2">Business address #1</p>
                  )}
                  <SplitAddressEditor
                    values={{
                      line1: displayData.companyInfo?.businessAddressLine1 ?? '',
                      line2: displayData.companyInfo?.businessAddressLine2 ?? '',
                      city: displayData.companyInfo?.businessAddressCity ?? '',
                      state: displayData.companyInfo?.businessAddressState ?? '',
                      zip: displayData.companyInfo?.businessAddressZip ?? ''
                    }}
                    onChange={(parts) => {
                      handleFieldChange('companyInfo', 'businessAddressLine1', parts.line1);
                      handleFieldChange('companyInfo', 'businessAddressLine2', parts.line2);
                      handleFieldChange('companyInfo', 'businessAddressCity', parts.city);
                      handleFieldChange('companyInfo', 'businessAddressState', parts.state);
                      handleFieldChange('companyInfo', 'businessAddressZip', parts.zip);
                      handleFieldChange('companyInfo', 'businessAddress', combineAddressParts(parts));
                    }}
                  />
                  <CongressionalDistrictField
                    editing
                    value={displayData.companyInfo?.businessAddressCongressionalDistrict}
                    onChange={(v) => handleFieldChange('companyInfo', 'businessAddressCongressionalDistrict', v)}
                  />
                  <AddressRoleCheckboxes
                    index={0}
                    total={totalBusinessAddresses}
                    values={displayData.companyInfo}
                    editing
                    onSelect={handleAddressRoleSelect}
                  />
                </div>
                {additionalBusinessAddresses.map((addr, idx) => (
                  <div key={idx} className="border rounded p-3 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-700">Business address #{idx + 2}</p>
                      <button
                        onClick={() => handleRemoveBusinessAddress(idx)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                    <SplitAddressEditor
                      values={{
                        line1: addr.line1 || '',
                        line2: addr.line2 || '',
                        city: addr.city || '',
                        state: addr.state || '',
                        zip: addr.zip || ''
                      }}
                      onChange={(parts) => updateBusinessAddress(idx, { ...parts, address: combineAddressParts(parts) })}
                    />
                    <CongressionalDistrictField
                      editing
                      value={addr.congressionalDistrict}
                      onChange={(v) => updateBusinessAddress(idx, { congressionalDistrict: v })}
                    />
                    <AddressRoleCheckboxes
                      index={idx + 1}
                      total={totalBusinessAddresses}
                      values={displayData.companyInfo}
                      editing
                      onSelect={handleAddressRoleSelect}
                    />
                  </div>
                ))}
                <button
                  onClick={() => handleFieldChange('companyInfo', 'additionalBusinessAddresses', [
                    ...additionalBusinessAddresses,
                    { line1: '', line2: '', city: '', state: '', zip: '', address: '', congressionalDistrict: '' }
                  ])}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + Add another business address
                </button>
              </>
            ) : (
              <>
                <SplitAddressDisplay
                  parts={{
                    line1: companyInfo.businessAddressLine1,
                    line2: companyInfo.businessAddressLine2,
                    city: companyInfo.businessAddressCity,
                    state: companyInfo.businessAddressState,
                    zip: companyInfo.businessAddressZip
                  }}
                  fallback={companyInfo.businessAddress}
                />
                <CongressionalDistrictField
                  editing={false}
                  value={companyInfo.businessAddressCongressionalDistrict}
                  onChange={() => {}}
                />
                <AddressRoleCheckboxes
                  index={0}
                  total={totalBusinessAddresses}
                  values={companyInfo}
                  editing={false}
                  onSelect={() => {}}
                />
                {additionalBusinessAddresses.map((addr, idx) => (
                  <div key={idx} className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-1">Address #{idx + 2}</p>
                    <SplitAddressDisplay
                      parts={{ line1: addr.line1, line2: addr.line2, city: addr.city, state: addr.state, zip: addr.zip }}
                      fallback={addr.address}
                    />
                    <CongressionalDistrictField
                      editing={false}
                      value={addr.congressionalDistrict}
                      onChange={() => {}}
                    />
                    <AddressRoleCheckboxes
                      index={idx + 1}
                      total={totalBusinessAddresses}
                      values={companyInfo}
                      editing={false}
                      onSelect={() => {}}
                    />
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-gray-700 mb-1">
              Performance Site Addresses
            </label>
            {isEditing ? (
              <>
                <label className="inline-flex items-center text-sm text-gray-700 mb-2">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={!!displayData.companyInfo?.performanceSiteSameAsBusiness}
                    onChange={(e) => {
                      const same = e.target.checked;
                      handleFieldChange('companyInfo', 'performanceSiteSameAsBusiness', same);
                      if (same) {
                        // Mirror business address into performance site fields
                        handleFieldChange('companyInfo', 'performanceSiteAddressLine1', displayData.companyInfo?.businessAddressLine1 || '');
                        handleFieldChange('companyInfo', 'performanceSiteAddressLine2', displayData.companyInfo?.businessAddressLine2 || '');
                        handleFieldChange('companyInfo', 'performanceSiteAddressCity', displayData.companyInfo?.businessAddressCity || '');
                        handleFieldChange('companyInfo', 'performanceSiteAddressState', displayData.companyInfo?.businessAddressState || '');
                        handleFieldChange('companyInfo', 'performanceSiteAddressZip', displayData.companyInfo?.businessAddressZip || '');
                        handleFieldChange('companyInfo', 'performanceSiteAddress', displayData.companyInfo?.businessAddress || '');
                        handleFieldChange('companyInfo', 'performanceSiteCongressionalDistrict', displayData.companyInfo?.businessAddressCongressionalDistrict || '');
                      }
                    }}
                  />
                  Primary site same as business address
                </label>
                {!displayData.companyInfo?.performanceSiteSameAsBusiness && (
                  <div className="border rounded p-3 mb-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Primary performance site</p>
                    <SplitAddressEditor
                      values={{
                        line1: displayData.companyInfo?.performanceSiteAddressLine1 ?? '',
                        line2: displayData.companyInfo?.performanceSiteAddressLine2 ?? '',
                        city: displayData.companyInfo?.performanceSiteAddressCity ?? '',
                        state: displayData.companyInfo?.performanceSiteAddressState ?? '',
                        zip: displayData.companyInfo?.performanceSiteAddressZip ?? ''
                      }}
                      onChange={(parts) => {
                        handleFieldChange('companyInfo', 'performanceSiteAddressLine1', parts.line1);
                        handleFieldChange('companyInfo', 'performanceSiteAddressLine2', parts.line2);
                        handleFieldChange('companyInfo', 'performanceSiteAddressCity', parts.city);
                        handleFieldChange('companyInfo', 'performanceSiteAddressState', parts.state);
                        handleFieldChange('companyInfo', 'performanceSiteAddressZip', parts.zip);
                        handleFieldChange('companyInfo', 'performanceSiteAddress', combineAddressParts(parts));
                      }}
                    />
                    <CongressionalDistrictField
                      editing
                      value={displayData.companyInfo?.performanceSiteCongressionalDistrict}
                      onChange={(v) => handleFieldChange('companyInfo', 'performanceSiteCongressionalDistrict', v)}
                    />
                  </div>
                )}
                {!!displayData.companyInfo?.performanceSiteSameAsBusiness && (
                  <div className="border rounded p-3 mb-3">
                    <p className="text-xs font-semibold text-gray-700">Primary performance site (business address)</p>
                    <CongressionalDistrictField
                      editing
                      value={displayData.companyInfo?.performanceSiteCongressionalDistrict}
                      onChange={(v) => handleFieldChange('companyInfo', 'performanceSiteCongressionalDistrict', v)}
                    />
                  </div>
                )}
                {(displayData.companyInfo?.additionalPerformanceSites || []).map((site, idx) => (
                  <div key={idx} className="border rounded p-3 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-700">Performance site #{idx + 2}</p>
                      <button
                        onClick={() => {
                          const next = [...(displayData.companyInfo?.additionalPerformanceSites || [])];
                          next.splice(idx, 1);
                          handleFieldChange('companyInfo', 'additionalPerformanceSites', next);
                        }}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                    <SplitAddressEditor
                      values={{
                        line1: site.line1 || '',
                        line2: site.line2 || '',
                        city: site.city || '',
                        state: site.state || '',
                        zip: site.zip || ''
                      }}
                      onChange={(parts) => {
                        const next = [...(displayData.companyInfo?.additionalPerformanceSites || [])];
                        next[idx] = { ...next[idx], ...parts, address: combineAddressParts(parts) };
                        handleFieldChange('companyInfo', 'additionalPerformanceSites', next);
                      }}
                    />
                    <CongressionalDistrictField
                      editing
                      value={site.congressionalDistrict}
                      onChange={(v) => {
                        const next = [...(displayData.companyInfo?.additionalPerformanceSites || [])];
                        next[idx] = { ...next[idx], congressionalDistrict: v };
                        handleFieldChange('companyInfo', 'additionalPerformanceSites', next);
                      }}
                    />
                  </div>
                ))}
                <button
                  onClick={() => {
                    const next = [...(displayData.companyInfo?.additionalPerformanceSites || []), { line1: '', line2: '', city: '', state: '', zip: '', address: '', congressionalDistrict: '' }];
                    handleFieldChange('companyInfo', 'additionalPerformanceSites', next);
                  }}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + Add another performance site
                </button>
              </>
            ) : (
              <>
                {companyInfo.performanceSiteSameAsBusiness ? (
                  <p className="text-gray-500 italic">Primary site same as business address</p>
                ) : (
                  <SplitAddressDisplay
                    parts={{
                      line1: companyInfo.performanceSiteAddressLine1,
                      line2: companyInfo.performanceSiteAddressLine2,
                      city: companyInfo.performanceSiteAddressCity,
                      state: companyInfo.performanceSiteAddressState,
                      zip: companyInfo.performanceSiteAddressZip
                    }}
                    fallback={companyInfo.performanceSiteAddress}
                  />
                )}
                <CongressionalDistrictField
                  editing={false}
                  value={companyInfo.performanceSiteCongressionalDistrict}
                  onChange={() => {}}
                />
                {(companyInfo.additionalPerformanceSites || []).map((site, idx) => (
                  <div key={idx} className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-1">Site #{idx + 2}</p>
                    <SplitAddressDisplay
                      parts={{
                        line1: site.line1,
                        line2: site.line2,
                        city: site.city,
                        state: site.state,
                        zip: site.zip
                      }}
                      fallback={site.address}
                    />
                    <CongressionalDistrictField
                      editing={false}
                      value={site.congressionalDistrict}
                      onChange={() => {}}
                    />
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-gray-700 mb-1">Business Description</label>
            {isEditing ? (
              <textarea
                value={displayData.companyInfo?.businessDescription || ''}
                onChange={(e) => handleFieldChange('companyInfo', 'businessDescription', e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-gray-900">{companyInfo.businessDescription || 'N/A'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              NAICS Code(s){' '}
              <a href={NAICS_SEARCH_URL} target="_blank" rel="noopener noreferrer" className="text-xs font-normal text-blue-600 hover:underline">
                (search NAICS codes)
              </a>
            </label>
            {isEditing ? (
              <input
                type="text"
                value={Array.isArray(displayData.companyInfo?.naicsCodes) ? displayData.companyInfo.naicsCodes.join(', ') : (displayData.companyInfo?.naicsCodes || '')}
                onChange={(e) => handleFieldChange('companyInfo', 'naicsCodes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-gray-900">{Array.isArray(companyInfo.naicsCodes) ? (companyInfo.naicsCodes.length > 0 ? companyInfo.naicsCodes.join(', ') : 'N/A') : (companyInfo.naicsCodes || 'N/A')}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Active Company Domain</label>
            {isEditing ? (
              <input
                type="text"
                value={displayData.companyInfo?.companyDomain || ''}
                onChange={(e) => handleFieldChange('companyInfo', 'companyDomain', e.target.value)}
                placeholder="e.g., company.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-gray-900">{companyInfo.companyDomain || 'N/A'}</p>
            )}
          </div>

        </div>

        {/* SO/AOR Information Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Signing Official / Authorized Organization Representative</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">SO/AOR Name</label>
              <p className="text-gray-900">{companyData.soAor || 'Not assigned'}</p>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number</label>
              <p className="text-gray-900">{signingOfficial.phone || 'N/A'}</p>
              {isEditing && isAdmin && (
                <p className="text-xs text-gray-500 mt-1">StreamLaunch's phone number — change it on the admin dashboard.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">SO/AOR Email</label>
              {isEditing && isAdmin ? (
                <>
                  <input
                    type="email"
                    value={displayData.companyInfo?.signingOfficialEmail ?? (companyData.soAorEmail || '')}
                    onChange={(e) => handleFieldChange('companyInfo', 'signingOfficialEmail', e.target.value)}
                    placeholder={companyInfo.companyDomain ? `so@${companyInfo.companyDomain}` : 'so@company.com'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use the signing official's address on your company domain, not the StreamLaunch address.
                  </p>
                </>
              ) : (
                <p className="text-gray-900">{signingOfficial.email || 'Not assigned'}</p>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Banks Section */}
      {canViewBankInfo && (
      <div className="card p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Banks</h2>
          {/* Adding a bank is a section-level action; editing happens inside each
              card so only one bank is ever changed at a time. */}
          <button
            onClick={handleAddBank}
            disabled={isAddingBank || editingBankIndex !== null}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Bank
          </button>
        </div>

        {(banks.length === 0 && !isAddingBank) ? (
          <p className="text-gray-500">No banks added yet. Click "+ Add Bank" to add banks.</p>
        ) : (
          <div className="space-y-4">
            {banks.map((bank, index) => renderBankCard(bank, index))}
            {isAddingBank && renderBankCard(editedBank, banks.length)}
          </div>
        )}
      </div>
      )}

      {/* Founder Information Section */}
      {visibleFounders.length > 0 && (
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Founder Information</h2>
        {/* <p className="text-gray-600 text-sm mb-4">
          {skipOnboarding
            ? 'Enter personal information for each founder.'
            : 'Personal information synced from the company formation form.'}
        </p> */}
        <div className="space-y-4">
          {founders.map((founder, index) => {
              if (!canViewFounder(founder)) return null;
              const isEditingThis = isEditingFounder === index;
              const displayFounder = isEditingThis ? editedFounder : founder;

              return (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {founder.fullName || founder.email || `Founder ${index + 1}`}
                    </h3>
                    {canEditFounder(founder) && !isEditingThis && (
                      <button
                        onClick={() => handleEditFounder(index)}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Edit
                      </button>
                    )}
                    {isEditingThis && (
                      <div className="flex gap-2">
                        <button
                          onClick={handleCancelFounderEdit}
                          className="px-3 py-1.5 text-sm bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveFounder}
                          disabled={savingFounder}
                          className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          {savingFounder ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Full Name</label>
                      {isEditingThis ? (
                        <input
                          type="text"
                          value={displayFounder.fullName || ''}
                          onChange={(e) => handleFounderFieldChange('fullName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{founder.fullName || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Email</label>
                      <p className="text-gray-900">{founder.email || 'N/A'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Phone</label>
                      {isEditingThis ? (
                        <input
                          type="tel"
                          value={displayFounder.cellPhone || ''}
                          onChange={(e) => handleFounderFieldChange('cellPhone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{founder.cellPhone || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Home Address</label>
                      {isEditingThis ? (
                        <SplitAddressEditor
                          values={{
                            line1: displayFounder.homeAddressLine1 ?? '',
                            line2: displayFounder.homeAddressLine2 ?? '',
                            city: displayFounder.homeAddressCity ?? '',
                            state: displayFounder.homeAddressState ?? '',
                            zip: displayFounder.homeAddressZip ?? ''
                          }}
                          onChange={(parts) => {
                            handleFounderFieldChange('homeAddressLine1', parts.line1);
                            handleFounderFieldChange('homeAddressLine2', parts.line2);
                            handleFounderFieldChange('homeAddressCity', parts.city);
                            handleFounderFieldChange('homeAddressState', parts.state);
                            handleFounderFieldChange('homeAddressZip', parts.zip);
                            handleFounderFieldChange('homeAddress', combineAddressParts(parts));
                          }}
                        />
                      ) : (
                        <SplitAddressDisplay
                          parts={{
                            line1: founder.homeAddressLine1,
                            line2: founder.homeAddressLine2,
                            city: founder.homeAddressCity,
                            state: founder.homeAddressState,
                            zip: founder.homeAddressZip
                          }}
                          fallback={founder.homeAddress}
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Date of Birth</label>
                      {isEditingThis ? (
                        <input
                          type="date"
                          value={displayFounder.dateOfBirth ? displayFounder.dateOfBirth.split('T')[0] : ''}
                          onChange={(e) => handleFounderFieldChange('dateOfBirth', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">
                          {founder.dateOfBirth ? new Date(founder.dateOfBirth + 'T00:00:00').toLocaleDateString() : 'N/A'}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">SSN</label>
                      {isEditingThis ? (
                        <input
                          type="text"
                          value={displayFounder.ssn || ''}
                          onChange={(e) => handleFounderFieldChange('ssn', e.target.value)}
                          placeholder="XXX-XX-XXXX"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          {founder.ssn ? (
                            <>
                              <p className="text-gray-900 font-mono">
                                {ssnVisible[index] ? founder.ssn : '***-**-****'}
                              </p>
                              <button
                                onClick={() => setSsnVisible(prev => ({ ...prev, [index]: !prev[index] }))}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                {ssnVisible[index] ? 'Hide' : 'Show'}
                              </button>
                            </>
                          ) : (
                            <p className="text-gray-900">N/A</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
          })}
        </div>
      </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Changes</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to save these changes to your company information?
              This will update all the fields you've modified.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Confirm Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyInfo;
